import { Injectable } from '@angular/core';
import { LOCALLY_HOSTED } from '../common/setup';
import { OFFLINE_STORES } from '../model';

const VERSION = 6; // increment this counter when a db table is changed/added

@Injectable()
export class IndexedDBService {

    private db: IDBDatabase;
    private dbName = 'servitlyOfflineDB';
    private generalStoreName = 'apiResponse';

    static OFFLINE_REQUEST_STORE_NAME = 'offlineRequest';
    static THING_DATA_STORE_NAME = 'thingData';
    static DB_NOT_REFRENCED_ERROR = 'IndexedDB not referenced';

    constructor() {
        if (LOCALLY_HOSTED) {
            this.init();
        }
    }

    init(): Promise<void> {
        if (this.db) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, VERSION);
            request.onerror = () => reject(`Error on opening IndexedDB: ${request.error}`);
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };
            request.onupgradeneeded = () => {
                this.db = request.result;
                if (!this.db.objectStoreNames.contains(this.generalStoreName)) {
                    this.db.createObjectStore(this.generalStoreName);
                }
                if (!this.db.objectStoreNames.contains(IndexedDBService.OFFLINE_REQUEST_STORE_NAME)) {
                    this.db.createObjectStore(IndexedDBService.OFFLINE_REQUEST_STORE_NAME, { keyPath: 'id', autoIncrement: true });
                }
                if (!this.db.objectStoreNames.contains(IndexedDBService.THING_DATA_STORE_NAME)) {
                    this.db.createObjectStore(IndexedDBService.THING_DATA_STORE_NAME, { keyPath: 'id' });
                }
                for (let store of OFFLINE_STORES) {
                    if (!this.db.objectStoreNames.contains(store.name)) {
                        const objectStore = this.db.createObjectStore(store.name, { keyPath: 'id' });
                        const indexField = store.indexField;
                        if (indexField) {
                            objectStore.createIndex(this.getIndexByFieldName(indexField), indexField, { unique: false });
                        }
                    }
                }
            };
        });
    }

    private getIndexByFieldName(field: string): string {
        return `${field}_idx`;
    }

    save(url: string, data: any): Promise<void> {
        return this.doSave(this.generalStoreName, data, url);
    }

    private doSave(storeName: string, data: any, key?: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(IndexedDBService.DB_NOT_REFRENCED_ERROR);
            }
            const tx = this.db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            store.put(data, key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    saveResource(storeName: string, data: any): Promise<void> {
        return this.doSave(storeName, data);
    }

    findByUrl(url: string): Promise<any> {
        return this.findByUniqueKey(this.generalStoreName, url);
    }

    findByUniqueKey(storeName: string, key: string): Promise<any> {
        return this.doFind(storeName, (store) => store.get(key));
    }

    findAll(storeName: string): Promise<any> {
        return this.doFind(storeName, (store) => store.getAll());
    }

    findByIndex(storeName: string, indexField: string, value: any): Promise<any> {
        return this.doFind(storeName, (store) => store.index(this.getIndexByFieldName(indexField)).getAll(value));
    }

    private doFind(storeName: string, computeRequest: (store: IDBObjectStore) => IDBRequest): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(IndexedDBService.DB_NOT_REFRENCED_ERROR);
                return;
            }
            const tx = this.db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const request = computeRequest(store);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    findWithKeys(storeName: string): Promise<{ key: string, value: any }[]> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(IndexedDBService.DB_NOT_REFRENCED_ERROR);
                return;
            }
            const tx = this.db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const request = store.openCursor();
            const results: { key: string, value: any }[] = [];
            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                if (cursor) {
                    results.push({ key: cursor.key as string, value: cursor.value });
                    cursor.continue();
                    return;
                }
                resolve(results);
            }
            request.onerror = () => reject(request.error)
        });
    }

    clearByIndex(storeName: string, indexFieldName: string, key: any): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(IndexedDBService.DB_NOT_REFRENCED_ERROR);
            }
            if (!this.db.objectStoreNames.contains(storeName)) {
                resolve();
            }
            const tx = this.db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const index = store.index(this.getIndexByFieldName(indexFieldName));
            const range = IDBKeyRange.only(key);
            const request = index.openCursor(range);
            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                } else {
                    resolve();
                }
            };
            request.onerror = () => reject(request.error);
        });
    }

    clearStore(storeName: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(IndexedDBService.DB_NOT_REFRENCED_ERROR);
            }
            if (!this.db.objectStoreNames.contains(storeName)) {
                resolve();
            }
            const tx = this.db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const request = store.clear();
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    clearById(storeName: string, id: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(IndexedDBService.DB_NOT_REFRENCED_ERROR);
            }
            if (!this.db.objectStoreNames.contains(storeName)) {
                resolve();
            }
            const tx = this.db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const request = store.delete(id);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

}